import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { SearchView } from "../../../components";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { getAllInvoices } from "@/data/requests/invoice-requests";

const COUNTRY = "Azerbaijan";
const MAX_AMOUNT = 10;

const STATUS_OPTIONS = [
  { value: "Approved", label: "Onaylandı" },
  { value: "Change Requested", label: "Değişiklik İstendi" },
  { value: "Troubled", label: "Sorunlu" },
  { value: "Invoice Uploaded", label: "Fatura Yüklendi" },
  { value: "Mail Sended", label: "Mail Gönderildi" },
  { value: "Accounting", label: "Muhasebe" },
  { value: "Auto Created Invoice", label: "Otomatik Oluşturulan Fatura" },
  { value: "OCR Completed", label: "OCR Tamamlandı" },
  { value: "LUCAREADY", label: "LUCA HAZIR" },
  { value: "LUCA", label: "LUCA" },
  { value: "WAITING", label: "BEKLİYOR" },
  { value: "InBank", label: "Bankada" },
  { value: "PAID-TIMEOUT", label: "ÖDEME-ZAMAN AŞIMI" },
  { value: "Paid", label: "Ödendi" },
  { value: "Not Paid", label: "Ödenmedi" },
  { value: "Return", label: "İade" },
  { value: "all", label: "Tümü" },
];

const DEFAULT_SEARCH = { name: "", email: "", lucaId: "", status: "" };

export function AzerbaijanLowAmountCommissionsScreen() {
  const { isAdvancedTable } = useTableView();
  const navigate = useNavigate();
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [searchParams, setSearchParams] = useState(DEFAULT_SEARCH);
  const [queryParams, setQueryParams] = useState(DEFAULT_SEARCH);

  const handleSearch = (item) => {
    const next = { ...searchParams, [item.key]: item.value };
    setSearchParams(next);
    setTimeout(() => {
      setQueryParams(next);
      setPage(1);
    }, 300);
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      "azerbaijan-low-amount-commissions",
      page,
      pageSize,
      queryParams,
      sortColumn,
      sortDirection,
    ],
    queryFn: () =>
      getAllInvoices({
        page,
        pageSize,
        countryFilter: COUNTRY,
        maxAmount: MAX_AMOUNT,
        nameFilter: queryParams.name,
        emailNameFilter: queryParams.email,
        lucaIdFilter: queryParams.lucaId,
        statusFilter: queryParams.status,
        sortColumn,
        sortDirection,
      }),
  });

  const columns = [
    {
      key: "clientName",
      label: "Müşteri Adı",
      accessor: (item) => item.commission?.client?.name || "",
    },
    {
      key: "influencerName",
      label: "Influencer",
      accessor: (item) => item.commission?.influencerName || "",
    },
    {
      key: "commissionEmail",
      label: "Komisyon E-postası",
      accessor: (item) => item.commission?.email || "",
    },
    { key: "invoiceId", label: "Fatura No", sortable: true },
    { key: "totalAmount", label: "Toplam Tutar", sortable: true },
    { key: "currency", label: "Para Birimi", sortable: true },
    {
      key: "country",
      label: "Ülke",
      accessor: (item) => item.commission?.country || "",
    },
    { key: "status", label: "Durum", sortable: true },
    {
      key: "createdAt",
      label: "Oluşturulma",
      sortable: true,
      accessor: (item) =>
        item.createdAt ? new Date(item.createdAt).toLocaleDateString("tr-TR") : "",
    },
    {
      key: "actions",
      label: "İşlemler",
      sortable: false,
      render: (row) => (
        <Button
          size="sm"
          onClick={() =>
            navigate(
              `/payments/${row.commission?.paymentId}/invoices/${row.id}`
            )
          }
        >
          Görüntüle
        </Button>
      ),
    },
  ];

  const defaultVisibleColumns = [
    "clientName",
    "commissionEmail",
    "totalAmount",
    "currency",
    "country",
    "status",
    "actions",
  ];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const pageSettings = {
    title: `Azerbaijan Commissions Under $${MAX_AMOUNT}`,
    actions: [
      {
        name: "Geri",
        color: "bg-red-100 hover:bg-red-400 hover:text-black text-red-800",
        icon: "add-icon",
        onClick: () => navigate("/dashboard"),
      },
    ],
  };

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
    >
      <div className="px-4 py-2 text-sm text-muted-foreground">
        Ülkesi şu olan komisyon detayları gösteriliyor:{" "}
        <strong>{COUNTRY}</strong> ve toplam tutarı şundan az:{" "}
        <strong>{MAX_AMOUNT}</strong> (currency ignored).
      </div>
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        resetDefault={() => {
          setSearchParams(DEFAULT_SEARCH);
          setQueryParams(DEFAULT_SEARCH);
          setPage(1);
        }}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={columns}
          columnVisibilityKey="azerbaijan-low-amount-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) =>
            navigate(`/payments/${item.commission?.paymentId}/invoices/${item.id}`)
          }
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={columns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) =>
            navigate(`/payments/${item.commission?.paymentId}/invoices/${item.id}`)
          }
        />
      )}
    </AppLayout>
  );
}
